import { combineRefs } from '@visiba/react-std';
import { Children, cloneElement, ForwardedRef, forwardRef, isValidElement, ReactNode, useEffect, useId, useState } from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { useText } from '../../../utils/hooks/use_text';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { Label } from '../base/label.component';
import { theme as commonTheme } from '../common_input.theme';
import { CellulaFormControl, useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { InputCheckboxGroupContext } from './_input_checkbox_group.context';
import { InputCheckbox, InputCheckboxProps, InputCheckboxVariants } from './input_checkbox.component';
import { theme } from './input_checkbox.theme';

const elements = {
	group: styled.fieldset`
		--item-direction: column;
		--item-gap: ${theme.getValue('itemsGap')};
		--items-margin: ${theme.getValue('itemsMargin')};

		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		display: flex;
		flex-direction: column;

		&[data-direction='horizontal'] {
			--item-direction: row;
			--item-gap: ${theme.getValue('itemsGap')};

			&[data-variant='none'] {
				--item-gap: ${theme.getValue('itemsHorizontalGap')};
			}
		}

		&[data-variant='outlined'] {
			--item-gap: ${theme.getValue('outlinedItemsGap')};
			--items-margin: 0;
		}
	`,
	legend: styled(Label)`
		margin-bottom: ${commonTheme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	items: styled.div`
		display: flex;
		flex-direction: var(--item-direction);
		gap: var(--item-gap);
		flex-wrap: wrap;
		margin: var(--items-margin) 0;
	`,
};

export type Direction = 'vertical' | 'horizontal';

interface InputCheckboxGroupProps {
	children: ReactNode;
	variant?: InputCheckboxVariants;
	direction?: Direction;
	label: string;
	hideLabel?: boolean;
	description?: string;
	required?: boolean;
	optional?: boolean;
	helperText?: string;
	readOnly?: boolean;
	disabled?: boolean;
	invalid?: boolean;
	invalidText?: string;
	name?: string;
	options?: RegisterOptions;
	value?: string[];
	defaultChecked?: boolean;
	onChange?: (value: string[]) => void;
}

const InputCheckboxGroupRoot = forwardRef(function InputCheckboxGroupRoot(
	{
		label,
		hideLabel,
		required,
		description,
		helperText,
		invalid,
		invalidText,
		variant = 'none',
		name,
		children,
		disabled,
		readOnly,
		onChange,
		value,
		direction = 'vertical',
		optional,
	}: InputCheckboxGroupProps,
	ref: ForwardedRef<HTMLFieldSetElement>,
): JSX.Element {
	const formContext = useFormContext();
	const id = useId();
	const descriptionId = `${id}-description`;
	const helpTextId = `${id}-help-text`;

	const [parsedChildren, setParsedChildren] = useState<ReactNode>([]);

	useEffect(
		function whenChildrenChanges() {
			if (formContext && name) {
				const newChildren: ReactNode[] = [];

				Children.toArray(children).map((child) => {
					if (!isValidElement(child) || child.type !== InputCheckbox) {
						return;
					}

					function handleChangeFactory(
						props: InputCheckboxProps,
						name: string,
						control: CellulaFormControl,
					): (value: boolean) => void {
						return (value: boolean): void => {
							let values = control.getValues(name);

							if (Array.isArray(values)) {
								if (value) {
									values.push(props.value);
								} else {
									values.splice(values.indexOf(props.value), 1);
								}
							} else {
								values = [props.value];
							}

							props.onChange?.(values);
							onChange?.(values);
						};
					}

					newChildren.push(
						cloneElement(child, {
							...child.props,
							name: undefined,
							onChange: handleChangeFactory(child.props, name, formContext.control),
							checked: child.props.checked ?? value?.includes(child.props.value),
						}),
					);
				});

				setParsedChildren(newChildren);
			} else {
				setParsedChildren(children);
			}
		},
		[children, formContext, name, onChange, value],
	);

	return (
		<InputCheckboxGroupContext.Provider
			value={{ invalid: invalid ?? false, disabled: disabled ?? false, readOnly: readOnly ?? false, variant, values: [], name }}
		>
			<elements.group
				data-variant={variant}
				data-direction={direction}
				ref={ref}
				tabIndex={-1}
				aria-describedby={[descriptionId, helpTextId].join(' ')}
				aria-required={required ? 'true' : undefined}
				aria-label={hideLabel ? label : undefined}
			>
				{label && !hideLabel ? (
					<elements.legend type='legend' optional={required ? false : optional}>
						{label}
					</elements.legend>
				) : null}
				<elements.description id={descriptionId}>{description}</elements.description>

				<elements.items>{parsedChildren}</elements.items>

				<HelpText id={helpTextId} invalid={invalid}>
					{invalid && invalidText ? invalidText : helperText}
				</HelpText>
			</elements.group>
		</InputCheckboxGroupContext.Provider>
	);
});

function CellulaInputCheckboxGroup(props: InputCheckboxGroupProps, ref: ForwardedRef<HTMLFieldSetElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	if (!formContext || props.name == null) {
		return <InputCheckboxGroupRoot {...props} />;
	}

	function handleChange(event: { target: { value: string[] } }): void {
		props.onChange?.(Array.isArray(event.target.value) ? event.target.value : []);
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			rules={{
				...props.options,
				onChange: handleChange,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				return (
					<InputCheckboxGroupRoot
						{...props}
						{...field}
						optional={inputPropsHelper.isOptional()}
						required={inputPropsHelper.isRequired()}
						readOnly={inputPropsHelper.isReadOnly()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

export const InputCheckboxGroup = forwardRef(CellulaInputCheckboxGroup);
export type { InputCheckboxGroupProps };
